'use client';

import { useEffect, useState } from 'react';

// Chromium-only. iOS Safari requires manual Add-to-Home-Screen and gives
// us no programmatic prompt — out of scope for v1.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'stacked.install.dismissed';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function recentlyDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  const raw = window.localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const at = Number(raw);
  if (!Number.isFinite(at)) return false;
  return Date.now() - at < DISMISS_TTL_MS;
}

export function InstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(true); // start hidden until we hear the event

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (recentlyDismissed()) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
      setDismissed(false);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!event || dismissed) return null;

  async function install() {
    if (!event) return;
    await event.prompt();
    await event.userChoice;
    setEvent(null);
    setDismissed(true);
  }

  function later() {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  }

  // Floating panel — eyebrow + display title + short note + a pair of
  // actions (amber install button, quiet "later" ghost). No generic
  // icon avatar; the typographic hierarchy carries the prompt.
  return (
    <div className="fixed bottom-24 right-5 z-30 w-[min(22rem,calc(100vw-2.5rem))] animate-rise-in space-y-3 rounded-xl border border-hairline bg-surface/70 p-5 shadow-xl backdrop-blur-sm lg:bottom-6">
      <div className="space-y-1.5">
        <p className="eyebrow">Install</p>
        <h3 className="font-display text-lg font-bold leading-tight tracking-tight">
          Stacked on your home screen.
        </h3>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Adds Stacked as an app so it loads instantly and works offline.
        </p>
      </div>
      <div className="flex items-baseline justify-end gap-5 pt-1">
        <button
          type="button"
          onClick={later}
          className="text-xs font-medium text-muted-foreground transition duration-fast ease-out-quint hover:text-foreground"
        >
          Later
        </button>
        <button
          type="button"
          onClick={install}
          className="rounded-full bg-accent px-4 py-1.5 text-xs font-semibold text-primary-foreground transition duration-fast ease-out-quint hover:shadow-glow hover:brightness-105"
        >
          Install
        </button>
      </div>
    </div>
  );
}
